import React, { useState, useEffect, useMemo, useCallback } from "react";
import Sidebar from '../components/SideBar';
import '../App.css';
import './productPage.css';
import Loading from "../components/loading";
import logoImage from '../images/Logo-Base-basic.png';
import { Table, Modal, Button } from 'react-bootstrap';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ENDPOINT_PATH } from '../config';
import {
  updateFramePrice, updateMatboardPrice, updateMediumPrice, updateGlassPrice, updateHangingPrice,
  getPricePerFoot, getMediumPrices, getGlassPrices, getMatboardPrices, updateArtistFee
} from "./updatePriceHelper";
import { addFrame, delFrameProduct } from "./productsHelper";

// Keep legacy styles (e.g. "Gallery Board", "Metallic") in the database/API for rollback,
// but hide them from the admin "Edit Price List" view to match the customer-facing vis tool.
const ADMIN_MATBOARD_PRICING_STYLES = ["Regular", "Acid Free"];

function tableColumnAriaSort(activeKey, activeDir, columnKey) {
  if (activeKey !== columnKey) return 'none';
  return activeDir === 'asc' ? 'ascending' : 'descending';
}

function FrameSortHeaderButton({ label, sortKey, activeKey, activeDir, onSort }) {
  const isPrimary = activeKey === sortKey;
  const showNeutral = !isPrimary;
  let arrows;
  if (showNeutral) {
    arrows = (
      <span className="frame-sort-arrows frame-sort-arrows--neutral" aria-hidden>
        <span className="frame-sort-arrow-up">▲</span>
        <span className="frame-sort-arrow-down">▼</span>
      </span>
    );
  } else if (activeDir === 'asc') {
    arrows = (
      <span className="frame-sort-arrows" aria-hidden>
        <span className="frame-sort-arrow-up frame-sort-arrow-up--solo">▲</span>
      </span>
    );
  } else {
    arrows = (
      <span className="frame-sort-arrows" aria-hidden>
        <span className="frame-sort-arrow-down frame-sort-arrow-down--solo">▼</span>
      </span>
    );
  }
  return (
    <button type="button" className="frame-sort-btn" onClick={() => onSort(sortKey)}>
      <span>{label}</span>
      {arrows}
    </button>
  );
}

function frameNormalizedString(product, fieldKey) {
  if (fieldKey === 'productId') return String(product.productID ?? '').trim();
  if (fieldKey === 'productName') return String(product.displayName ?? '').trim();
  if (fieldKey === 'frameType') return String(product.frameType ?? '').trim();
  return '';
}

function compareFrameStringsForSort(a, b, fieldKey, ascending) {
  const sa = frameNormalizedString(a, fieldKey);
  const sb = frameNormalizedString(b, fieldKey);
  const aEmpty = sa === '';
  const bEmpty = sb === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  const cmp = sa.localeCompare(sb, undefined, { sensitivity: 'base', numeric: true });
  return ascending ? cmp : -cmp;
}

function frameThicknessMin(product) {
  const arr = product.frameThicknessAvailable;
  if (!Array.isArray(arr) || arr.length === 0) return null;
  let min = Infinity;
  for (const x of arr) {
    const n = parseFloat(String(x).trim());
    if (Number.isFinite(n)) min = Math.min(min, n);
  }
  return min === Infinity ? null : min;
}

function frameThicknessDisplay(product) {
  const arr = product.frameThicknessAvailable;
  if (!Array.isArray(arr) || arr.length === 0) return '';
  return arr.map(String).join(', ');
}

function compareFrameThickness(a, b, ascending) {
  const aMin = frameThicknessMin(a);
  const bMin = frameThicknessMin(b);
  const aEmpty = aMin == null;
  const bEmpty = bMin == null;
  if (aEmpty && bEmpty) {
    const cmp = frameThicknessDisplay(a).localeCompare(frameThicknessDisplay(b), undefined, {
      sensitivity: 'base',
      numeric: true,
    });
    return ascending ? cmp : -cmp;
  }
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (aMin !== bMin) {
    const cmp = aMin - bMin;
    return ascending ? cmp : -cmp;
  }
  const tie = frameThicknessDisplay(a).localeCompare(frameThicknessDisplay(b), undefined, {
    sensitivity: 'base',
    numeric: true,
  });
  return ascending ? tie : -tie;
}

function framePriceNumeric(product) {
  const p = product.price;
  const n = typeof p === 'number' && Number.isFinite(p) ? p : parseFloat(String(p));
  return Number.isFinite(n) ? n : 0;
}

function compareFramesForTable(a, b, primaryKey, primaryDir) {
  const asc = primaryDir === 'asc';
  if (primaryKey === 'pricePerFoot') {
    const diff = framePriceNumeric(b) - framePriceNumeric(a);
    if (!asc) {
      if (diff !== 0) return diff;
    } else if (diff !== 0) {
      return -diff;
    }
  } else if (primaryKey === 'thickness') {
    const c = compareFrameThickness(a, b, asc);
    if (c !== 0) return c;
  } else {
    const strCmp = compareFrameStringsForSort(a, b, primaryKey, asc);
    if (strCmp !== 0) return strCmp;
  }
  return String(b.productID || '').localeCompare(String(a.productID || ''));
}

function sortFrameProductList(list, primaryKey, primaryDir) {
  return [...list].sort((a, b) => compareFramesForTable(a, b, primaryKey, primaryDir));
}

function matboardNormalizedString(product, fieldKey) {
  if (fieldKey === 'productId') return String(product.productID ?? '').trim();
  if (fieldKey === 'productName') return String(product.displayName ?? '').trim();
  return String(product.style ?? '').trim();
}

function compareMatboardStringsForSort(a, b, fieldKey, ascending) {
  const sa = matboardNormalizedString(a, fieldKey);
  const sb = matboardNormalizedString(b, fieldKey);
  const aEmpty = sa === '';
  const bEmpty = sb === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  const cmp = sa.localeCompare(sb, undefined, { sensitivity: 'base', numeric: true });
  return ascending ? cmp : -cmp;
}

function compareMatboardsForTable(a, b, primaryKey, primaryDir) {
  const asc = primaryDir === 'asc';
  const strCmp = compareMatboardStringsForSort(a, b, primaryKey, asc);
  if (strCmp !== 0) return strCmp;
  return String(b.productID || '').localeCompare(String(a.productID || ''));
}

function sortMatboardProductList(list, primaryKey, primaryDir) {
  return [...list].sort((a, b) => compareMatboardsForTable(a, b, primaryKey, primaryDir));
}

function coercePriceNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = parseFloat(String(value ?? '').trim());
  return Number.isFinite(parsed) ? parsed : 0;
}

const AdminNavbar = () => {
  return (
    <div className="navWrapper">
      <div className="AdminNav">
        <div>
          <a href="/" className="NavLink">
            <div className="AdminLogo d-flex justify-content-end">
              <img src={logoImage} alt='logo' className="NavLogo" />
            </div>
            <div className="AdminLogoText font-2 ps-2 fontLogo">mycustomframe</div>
          </a>
        </div>
      </div>
    </div>
  );
};

const ProductPage = () => {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState({ frames: [], matboards: [], glass: [], hanging: [] });
  const [imageSizeList, setImageSizeList] = useState([])
  const [pricing, setPricing] = useState({ matboards: {}, mediums: {}, glass: {} });
  const [canvasPricing, setCanvasPricing] = useState({ '1 1/2 inch thick': {}, '3/4 inch thick': {} });
  const [artistFee, setArtistFee] = useState(0);

  // User type detection for read-only mode
  const [userType, setUserType] = useState(null);
  const user = JSON.parse(localStorage.getItem('user'));

  // Fetch user type on mount
  useEffect(() => {
    const fetchUserType = async () => {
      try {
        if (user && user.uid) {
          const response = await fetch(ENDPOINT_PATH + `/api/get-account-type?user=` + encodeURIComponent(user.uid));
          const accountType = await response.json();
          setUserType(accountType.toLowerCase());
        }
      } catch (error) {
        console.error("Error fetching user type:", error);
      }
    };

    fetchUserType();
  }, [user]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const responses = await Promise.all([
          fetch(`${ENDPOINT_PATH}/api/frames`).then(res => res.json()),
          fetch(`${ENDPOINT_PATH}/api/mediums`).then(res => res.json()),
          fetch(`${ENDPOINT_PATH}/api/matboards`).then(res => res.json()),
          fetch(`${ENDPOINT_PATH}/api/hanging-styles`).then(res => res.json()),
          fetch(`${ENDPOINT_PATH}/api/glass-types`).then(res => res.json()),
          fetch(`${ENDPOINT_PATH}/api/artist-fee`).then(res => res.json()),
        ]);

        const [framesData, mediumsData, matboardsData, hangingStylesData, glassTypesData, artistFeeData] = responses;

        const framesWithPrices = await Promise.all(
          framesData.filter(frame => frame.frameName !== 'noSelectedFrame').map(async (frame) => {
            const priceData = await getPricePerFoot(frame.frameName);
            return {
              productID: frame.frameName,
              price: priceData?.pricePerFoot ?? 0,
              ...frame,
            };
          })
        );

        const hangingWithPrices = await Promise.all(
          hangingStylesData.map(async (hanging) => {
            const response = await fetch(ENDPOINT_PATH + `/api/hanging-price?hangingStyle=` + encodeURIComponent(hanging));
            const hangingPriceData = await response.json();
            return {
              displayName: hanging,
              price: hangingPriceData
            }
          })
        );

        setProducts({
          frames: framesWithPrices,
          mediums: mediumsData.map(medium => ({
            displayName: medium.mediumName,
          })),
          matboards: matboardsData.map(matboard => ({
            productID: matboard.internalName,
            ...matboard,
          })),
          glass: glassTypesData.map(glassType => ({
            displayName: glassType,
          })),
          hanging: hangingWithPrices
        });

        setArtistFee(artistFeeData);

        setLoading(false);

      } catch (error) {
        setLoading(false);
        console.error('Error fetching products:', error);
      }
    };

    const fetchImageSizes = async () => {
      try {
        const response = await fetch(`${ENDPOINT_PATH}/api/image-sizes`);
        const data = await response.json();

        const sortedSizes = data.sort((a, b) => {
          if (a.width !== b.width) {
            return a.width - b.width;
          }
          // If width is the same, compare height
          return a.height - b.height;
        });

        setImageSizeList(sortedSizes);

      } catch (error) {
        console.error('Error fetching products price:', error);
      }
    };

    const fetchProductPrices = async () => {
      const matboardsPriceData = await getMatboardPrices();
      const mediumsPriceData = await getMediumPrices();
      const glassPriceData = await getGlassPrices();

      setPricing({
        matboards: matboardsPriceData.prices,
        mediums: mediumsPriceData.prices,
        glass: glassPriceData.prices
      });

      // Fetch canvas prices split by stretching style
      try {
        // We re-use the existing get-all-medium-prices but need per-stretchingStyle breakdown.
        // Fetch directly from a dedicated canvas-prices endpoint or replicate here.
        const [snap1Res, snap2Res] = await Promise.all([
          fetch(`${ENDPOINT_PATH}/api/get-canvas-prices?stretchingStyle=` + encodeURIComponent('1 1/2 inch thick')).then(r => r.json()),
          fetch(`${ENDPOINT_PATH}/api/get-canvas-prices?stretchingStyle=` + encodeURIComponent('3/4 inch thick')).then(r => r.json()),
        ]);
        setCanvasPricing({
          '1 1/2 inch thick': snap1Res.prices || {},
          '3/4 inch thick': snap2Res.prices || {},
        });
      } catch (e) {
        console.error('Error fetching canvas prices:', e);
      }
    }

    fetchProducts();
    fetchImageSizes();
    fetchProductPrices();
  }, []);

  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeCategory, setActiveCategory] = useState('frames');
  /** Frames table only: default product ID A→Z. */
  const [frameSort, setFrameSort] = useState({ key: 'productId', dir: 'asc' });
  /** Matboards list view (not price grid): default product ID A→Z. */
  const [matboardSort, setMatboardSort] = useState({ key: 'productId', dir: 'asc' });

  const itemsPerPage = 10;

  // Modal states
  const [showProductModal, setShowProductModal] = useState(false);
  const [showMatboardPrices, setShowMatboardPrices] = useState(false);
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [priceChanges, setPriceChanges] = useState({});
  const [priceInputDrafts, setPriceInputDrafts] = useState({});
  const [bulkOperations, setBulkOperations] = useState({});
  const [bulkAmounts, setBulkAmounts] = useState({});
  const [isEditingCanvasPrice, setIsEditingCanvasPrice] = useState(false);
  const [canvasPriceChanges, setCanvasPriceChanges] = useState({});
  const [canvasPriceDrafts, setCanvasPriceDrafts] = useState({});
  const [canvasBulkOperations, setCanvasBulkOperations] = useState({});
  const [canvasBulkAmounts, setCanvasBulkAmounts] = useState({});


  // Product categories
  const categories = ['frames', 'matboards', 'mediums', 'glass', 'hanging', 'artist fee'];

  const filteredProducts = useMemo(() => {
    const base = (products[activeCategory] || [])
      .filter((product) =>
        activeCategory !== 'matboards' ? true : ADMIN_MATBOARD_PRICING_STYLES.includes(product?.style)
      )
      .filter(
        (product) =>
          product?.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          product?.productID?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    if (activeCategory === 'frames') return sortFrameProductList(base, frameSort.key, frameSort.dir);
    if (activeCategory === 'matboards') return sortMatboardProductList(base, matboardSort.key, matboardSort.dir);
    return base;
  }, [products, activeCategory, searchQuery, frameSort, matboardSort]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleFrameSortClick = useCallback((clickedKey) => {
    setCurrentPage(1);
    setFrameSort((prev) => {
      if (clickedKey === 'pricePerFoot') {
        if (prev.key === 'pricePerFoot') {
          return { key: 'pricePerFoot', dir: prev.dir === 'asc' ? 'desc' : 'asc' };
        }
        return { key: 'pricePerFoot', dir: 'desc' };
      }
      if (prev.key === clickedKey) {
        return { key: clickedKey, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key: clickedKey, dir: 'asc' };
    });
  }, []);

  const handleMatboardSortClick = useCallback((clickedKey) => {
    setCurrentPage(1);
    setMatboardSort((prev) => {
      if (prev.key === clickedKey) {
        return { key: clickedKey, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key: clickedKey, dir: 'asc' };
    });
  }, []);

  // Calculate total pages
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  // Paginate filtered products
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };


  const handleEditPricing = () => {
    setIsEditingPrice(true)
    setPriceChanges({});
    setPriceInputDrafts({});
    setBulkOperations({});
    setBulkAmounts({});
  };

  const getSizePriceInputValue = (product, size) => {
    const draftValue = priceInputDrafts[product]?.[size];
    if (draftValue !== undefined) return draftValue;

    const changedValue = priceChanges[product]?.[size];
    if (changedValue !== undefined) return coercePriceNumber(changedValue).toFixed(2);

    return coercePriceNumber(pricing[activeCategory]?.[product]?.[size]).toFixed(2);
  };

  const handlePriceChange = (e, product, size) => {
    const newPrice = e.target.value;
    setPriceInputDrafts((prev) => ({
      ...prev,
      [product]: {
        ...(prev[product] || {}),
        [size]: newPrice,
      },
    }));
  };

  const handleBlur = (product, size) => {
    const rawValue = priceInputDrafts[product]?.[size] ?? getSizePriceInputValue(product, size);
    let newPrice = parseFloat(String(rawValue).trim());

    if (isNaN(newPrice) || newPrice < 0) {
      newPrice = 0;
    }

    const formattedPrice = newPrice.toFixed(2);

    setPriceInputDrafts((prev) => ({
      ...prev,
      [product]: {
        ...(prev[product] || {}),
        [size]: formattedPrice,
      },
    }));

    setPriceChanges((prev) => ({
      ...prev,
      [product]: {
        ...(prev[product] || {}),
        [size]: parseFloat(formattedPrice),
      },
    }));
  };


  const handleSave = (product) => {
    const productChanges = priceChanges[product];
    const hasProductChanges = productChanges && Object.keys(productChanges).length > 0;

    if (pricing[activeCategory] && pricing[activeCategory][product] && hasProductChanges) {
      setPricing((prevPricing) => {
        const updatedState = { ...prevPricing };
        updatedState[activeCategory][product] = {
          ...updatedState[activeCategory][product], // Retain existing pricing details for the product
          ...productChanges, // Apply price changes
        };
        return updatedState;
      });
    }

    // Update price in database
    if (hasProductChanges) {

      Object.entries(productChanges).forEach(([frameSize, price]) => {

        switch (activeCategory) {
          case 'matboards':
            updateMatboardPrice(frameSize, product, price);
            break;
          case 'mediums':
            updateMediumPrice(product, frameSize, price);
            break;
          case 'glass':
            updateGlassPrice(frameSize, product, price);
            break;
          default:
            console.log("Unknown category");
            break;
        }
      });
    }

    setIsEditingPrice(false);
    setPriceInputDrafts({});
    setBulkOperations({});
    setBulkAmounts({});
    setPriceChanges({}); // Clear any stored price changes
  };

  const handleCancel = () => {
    setIsEditingPrice(false);
    setPriceInputDrafts({});
    setBulkOperations({});
    setBulkAmounts({});
    setPriceChanges({});
  };

  const handleCanvasEditPricing = () => {
    setIsEditingCanvasPrice(true);
    setCanvasPriceChanges({});
    setCanvasPriceDrafts({});
    setCanvasBulkOperations({});
    setCanvasBulkAmounts({});
  };

  const getCanvasPriceInputValue = (style, size) => {
    const draftValue = canvasPriceDrafts[style]?.[size];
    if (draftValue !== undefined) return draftValue;

    const changedValue = canvasPriceChanges[style]?.[size];
    if (changedValue !== undefined) return coercePriceNumber(changedValue).toFixed(2);

    return coercePriceNumber(canvasPricing[style]?.[size]).toFixed(2);
  };

  const handleCanvasPriceChange = (e, style, size) => {
    const raw = e.target.value;
    setCanvasPriceDrafts((prev) => ({
      ...prev,
      [style]: {
        ...(prev[style] || {}),
        [size]: raw,
      },
    }));
  };

  const handleCanvasBlur = (style, size) => {
    const rawValue = canvasPriceDrafts[style]?.[size] ?? getCanvasPriceInputValue(style, size);
    let newPrice = parseFloat(String(rawValue).trim());
    if (isNaN(newPrice) || newPrice < 0) newPrice = 0;
    const formattedPrice = newPrice.toFixed(2);

    setCanvasPriceDrafts((prev) => ({
      ...prev,
      [style]: {
        ...(prev[style] || {}),
        [size]: formattedPrice,
      },
    }));

    setCanvasPriceChanges((prev) => ({
      ...prev,
      [style]: {
        ...(prev[style] || {}),
        [size]: parseFloat(formattedPrice),
      },
    }));
  };

  const handleCanvasSave = async (style) => {
    const changes = canvasPriceChanges[style] || {};
    for (const [size, price] of Object.entries(changes)) {
      await fetch(`${ENDPOINT_PATH}/api/update-canvas-price?imageSize=${encodeURIComponent(size)}&stretchingStyle=${encodeURIComponent(style)}&newPrice=${price}`, { method: 'PATCH' });
    }
    setCanvasPricing(prev => ({
      ...prev,
      [style]: { ...prev[style], ...changes }
    }));
    setIsEditingCanvasPrice(false);
    setCanvasPriceChanges({});
    setCanvasPriceDrafts({});
    setCanvasBulkOperations({});
    setCanvasBulkAmounts({});
  };

  const handleCanvasCancel = () => {
    setIsEditingCanvasPrice(false);
    setCanvasPriceChanges({});
    setCanvasPriceDrafts({});
    setCanvasBulkOperations({});
    setCanvasBulkAmounts({});
  };


  const handleDeleteProduct = (product) => {
    if (window.confirm(`Are you sure you want to delete ${product.productID}?`)) {
      delFrameProduct(product.productID);
      setProducts((prevProducts) => {
        const updatedCategory = prevProducts[activeCategory].filter(
          (p) => p.productID !== product.productID
        );
        return { ...prevProducts, [activeCategory]: updatedCategory };
      });
    }
  };

  const handleImageUpload = (e, setImageState) => {
    const file = e.target.files[0];

    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageState(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };


  const ProductModal = ({
    handleImageUpload,
    onProductSaved
  }) => {
    // Available frame sizes
    const FRAME_SIZES = imageSizeList.map(size => size.string);

    // Product State
    const [productName, setProductName] = useState('');
    const [productID, setProductID] = useState('');
    const [uploadedImage, setUploadedImage] = useState(null);
    const [multipleFiles, setMultipleFiles] = useState({});

    // Frame-related States
    const [frameType, setFrameType] = useState('standard');
    const [frameThicknesses, setFrameThicknesses] = useState([]);
    const [pricePerFoot, setPricePerFoot] = useState('');

    // Validation State
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    // Thickness options
    const thicknessOptions = ['1', '1.25', '1.5', '2'];

    // Convert base64 to File object
    const base64ToFile = (base64Str, filename) => {
      try {
        const arr = base64Str.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }

        return new File([u8arr], filename, { type: mime });
      } catch (error) {
        console.error('Error converting base64 to file:', error);
        return null;
      }
    };


    // Reset form method
    const resetForm = () => {
      setProductName('');
      setProductID('');
      setUploadedImage(null);
      setMultipleFiles({});
      setFrameType('standard');
      setFrameThicknesses([]);
      setPricePerFoot('');
      setErrors({});

      // Initialize multipleFiles with null for all sizes
      const initialFiles = {};
      FRAME_SIZES.forEach(size => {
        initialFiles[size] = null;
      });
      setMultipleFiles(initialFiles);
    };

    const onClose = () => {
      resetForm(); // Reset form fields
      setShowProductModal(false); // Close modal
    };

    // Validate form
    const validateForm = () => {
      const newErrors = {};

      if (!productName) newErrors.productName = 'Product name is required';
      if (!productID) newErrors.productID = 'Product ID is required';
      if (!uploadedImage) newErrors.image = 'Product image is required';
      if (frameThicknesses.length === 0) newErrors.frameThicknesses = 'Select at least one thickness';

      // Validate price
      const parsedPrice = parseFloat(pricePerFoot);
      if (isNaN(parsedPrice) || parsedPrice <= 0) {
        newErrors.pricePerFoot = 'Price per foot must be a valid positive number';
      }
      else {
        setPricePerFoot(parsedPrice);
      }

      // Validate thickness-size file uploads
      FRAME_SIZES.forEach(size => {
        frameThicknesses.forEach(thickness => {
          const key = `${thickness}-${size}`;
          if (!multipleFiles[key]) {
            newErrors.thicknessFiles = `Please upload files for all size-thickness combinations`;
          }
        });
      });

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    // Handle file upload for sizes
    const handleFileUpload = (e, size, thickness) => {
      const file = e.target.files[0];
      setMultipleFiles(prev => ({
        ...prev,
        [`${thickness}-${size}`]: file
      }));
    };

    // Handle thickness selection
    const handleThicknessChange = (thickness) => {
      setFrameThicknesses(prev =>
        prev.includes(thickness)
          ? prev.filter(t => t !== thickness)
          : [...prev, thickness]
      );
    };

    // Save product
    const handleSave = async (e) => {
      e.preventDefault();

      // Validate form
      if (!validateForm()) {
        console.log("Error Validating Form")
        return;
      }

      setIsLoading(true);

      try {
        const availableThickness = frameThicknesses.map(thickness => Number(thickness))

        // Prepare product data
        const productData = {
          productID: productID,
          displayName: productName,
          iconUrl: uploadedImage,
          frameType: frameType,
          frameThicknessAvailable: availableThickness,
          price: parseFloat(pricePerFoot)
        };

        // Convert base64 image to File if needed
        let visToolIcon = null;
        if (uploadedImage && uploadedImage.startsWith('data:')) {
          visToolIcon = base64ToFile(uploadedImage, `${productID}_icon.png`);
        }

        // Add frame using the existing addFrame function
        await addFrame(
          productID,               // frameName
          productName,             // frameDisplayName
          pricePerFoot,            // pricePerFoot
          frameType,               // frameType
          visToolIcon,             // visToolIcon
          availableThickness,      // frameThicknessAvailable
          multipleFiles            // frameFiles
        );

        // Notify parent component
        onProductSaved(productData);

        // Close modal
        onClose();
      } catch (error) {
        console.error('Error saving product:', error);
        alert('Failed to save product. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <Modal show={true} onHide={onClose} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{'New Product'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            {/* Full-width Product Form */}
            <div className="col-12">
              <form onSubmit={handleSave}>
                {/* Product Name */}
                <div className="form-group mb-3">
                  <label>Product Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${errors.productName ? 'is-invalid' : ''}`}
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                  />
                  {errors.productName && <div className="invalid-feedback">{errors.productName}</div>}
                </div>

                {/* Product ID */}
                <div className="form-group mb-3">
                  <label>Product ID <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${errors.productID ? 'is-invalid' : ''}`}
                    value={productID}
                    onChange={(e) => setProductID(e.target.value)}
                  />
                  {errors.productID && <div className="invalid-feedback">{errors.productID}</div>}
                </div>

                {/* Frame Type */}
                <div className="form-group mb-3">
                  <label>Frame Type</label>
                  <select
                    className="form-control"
                    value={frameType}
                    onChange={(e) => setFrameType(e.target.value)}
                  >
                    <option value="standard">Standard</option>
                    <option value="floater">Floater</option>
                  </select>
                </div>

                {/* Frame Thickness */}
                <div className="form-group mb-3">
                  <label>Frame Thickness <span className="text-danger">*</span></label>
                  <div>
                    {thicknessOptions.map((thickness) => (
                      <div key={thickness} className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`thickness-${thickness}`}
                          value={thickness}
                          checked={frameThicknesses.includes(thickness)}
                          onChange={() => handleThicknessChange(thickness)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`thickness-${thickness}`}
                        >
                          {thickness} mm
                        </label>
                      </div>
                    ))}
                    {errors.frameThicknesses && <div className="text-danger">{errors.frameThicknesses}</div>}
                  </div>
                </div>

                {/*Vis Tool Icon */}
                <div className="form-group mb-3">
                  <label>Vis Tool Icon</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control form-control-sm"
                    onChange={(e) => handleImageUpload(e, setUploadedImage)}
                  />
                </div>

                {/* Thickness-Size File Uploads */}
                {frameThicknesses.length > 0 && (
                  <div className="form-group mb-3">
                    <label>Size-Thickness Image Uploads<span className="text-danger">*</span></label>
                    {FRAME_SIZES.map(size => (
                      <div key={size} className="mb-3">
                        <h6><strong style={{ color: '#ff4172' }}>{size} Size</strong>
                        </h6>
                        {frameThicknesses.map(thickness => (
                          <div
                            key={`${thickness}-${size}`}
                            className="d-flex align-items-center mb-2"
                          >
                            <span className="m-2">{thickness} mm:</span>
                            <input
                              type="file"
                              className="form-control form-control-sm"
                              onChange={(e) => handleFileUpload(e, size, thickness)}
                            />
                            {multipleFiles[`${size}-${thickness}`] && (
                              <small className="ms-2">
                                {multipleFiles[`${thickness}-${size}`].length} file(s) selected
                              </small>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                    {errors.thicknessFiles && <div className="text-danger">{errors.thicknessFiles}</div>}
                  </div>
                )}

                {/* Price per Foot */}
                <div className="form-group mb-3">
                  <label>Price per Foot <span className="text-danger">*</span></label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="number"
                      className={`form-control ${errors.pricePerFoot ? 'is-invalid' : ''}`}
                      placeholder="0.00"
                      value={pricePerFoot}
                      onChange={(e) => setPricePerFoot(e.target.value)}
                      step="0.01"
                    />
                    {errors.pricePerFoot && <div className="invalid-feedback">{errors.pricePerFoot}</div>}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Product'}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  const FramesTable = ({ userType }) => {
    const [isEditing, setIsEditing] = useState(null);
    const [priceChange, setPriceChange] = useState({});

    const handlePriceChange = (productID, newPrice) => {
      const parsedPrice = parseFloat(newPrice);
      if (parsedPrice >= 0) {
        setPriceChange({ [productID]: parsedPrice });
      }
    };
    const handleSave = (productID) => {
      setProducts((prevProducts) => {
        const updatedFrames = prevProducts.frames.map((product) => {
          if (product.productID === productID) {
            return { ...product, price: priceChange[productID] };
          }
          return product;
        });

        return { ...prevProducts, frames: updatedFrames };
      });

      // Update in database 
      updateFramePrice(productID, priceChange[productID]);

      setIsEditing(null);
      setPriceChange({});
    }

    const handleCancel = () => {
      setPriceChange({});
      setIsEditing(null);
    }



    const sortKey = frameSort.key;
    const sortDir = frameSort.dir;

    return (
      <div className="frames-sortable-wrap">
      <Table striped bordered hover className="product-table" >
        <thead>
          <tr>
            <th style={{ width: "8%" }} scope="col">PREVIEW</th>
            <th
              style={{ width: "12%" }}
              scope="col"
              aria-sort={tableColumnAriaSort(sortKey, sortDir, 'productId')}
              className={`frame-sort-th ${sortKey === 'productId' ? 'frame-sort-th--active' : ''}`}
            >
              <FrameSortHeaderButton
                label="PRODUCT ID"
                sortKey="productId"
                activeKey={sortKey}
                activeDir={sortDir}
                onSort={handleFrameSortClick}
              />
            </th>
            <th
              style={{ width: "20%" }}
              scope="col"
              aria-sort={tableColumnAriaSort(sortKey, sortDir, 'productName')}
              className={`frame-sort-th ${sortKey === 'productName' ? 'frame-sort-th--active' : ''}`}
            >
              <FrameSortHeaderButton
                label="PRODUCT NAME"
                sortKey="productName"
                activeKey={sortKey}
                activeDir={sortDir}
                onSort={handleFrameSortClick}
              />
            </th>
            <th
              style={{ width: "12%" }}
              scope="col"
              aria-sort={tableColumnAriaSort(sortKey, sortDir, 'frameType')}
              className={`frame-sort-th ${sortKey === 'frameType' ? 'frame-sort-th--active' : ''}`}
            >
              <FrameSortHeaderButton
                label="FRAME TYPE"
                sortKey="frameType"
                activeKey={sortKey}
                activeDir={sortDir}
                onSort={handleFrameSortClick}
              />
            </th>
            <th
              style={{ width: "13%" }}
              scope="col"
              aria-sort={tableColumnAriaSort(sortKey, sortDir, 'thickness')}
              className={`frame-sort-th ${sortKey === 'thickness' ? 'frame-sort-th--active' : ''}`}
            >
              <FrameSortHeaderButton
                label="THICKNESS"
                sortKey="thickness"
                activeKey={sortKey}
                activeDir={sortDir}
                onSort={handleFrameSortClick}
              />
            </th>
            <th
              style={{ width: "12%" }}
              scope="col"
              aria-sort={tableColumnAriaSort(sortKey, sortDir, 'pricePerFoot')}
              className={`frame-sort-th ${sortKey === 'pricePerFoot' ? 'frame-sort-th--active' : ''}`}
            >
              <FrameSortHeaderButton
                label="PRICE PER FOOT"
                sortKey="pricePerFoot"
                activeKey={sortKey}
                activeDir={sortDir}
                onSort={handleFrameSortClick}
              />
            </th>
            {userType !== 'employee' && <th style={{ width: "10%" }} scope="col">ACTIONS</th>}
          </tr>
        </thead>
        <tbody>
          {paginatedProducts.map((product) => (
            <tr key={product.productID}>
              <td>
                <img
                  src={product.iconUrl}
                  alt={product.displayName}
                  className="product-image"
                  loading="lazy"
                  style={{
                    width: '50px', height: '50px', objectFit: 'cover',
                    border: '1px solid #ddd', backgroundColor: 'white',
                  }}
                />
              </td>
              <td>{product.productID}</td>
              <td>{product.displayName}</td>
              <td>{product.frameType}</td>
              <td>{product.frameThicknessAvailable.join(', ')}</td>
              {isEditing === product.productID ? (
                <td key={product.productID}>
                  <input
                    type="number"
                    className="price-input"
                    value={priceChange[product.productID] ?? product.price}
                    onChange={(e) => handlePriceChange(product.productID, e.target.value)}
                    disabled={userType === 'employee'}
                  />
                </td>
              ) :
                (<td>${(product.price)}</td>)}
              {userType !== 'employee' && (
                <td>
                  <div className="d-flex justify-content-center align-items-center gap-2">
                    {isEditing === product.productID ? (
                      <div className="d-flex justify-content-center align-items-center gap-2">
                        <Button variant="primary" size="sm" onClick={() => handleSave(product.productID)}>Save</Button>
                        <Button variant="secondary" size="sm" onClick={() => handleCancel()}>Cancel</Button>
                      </div>
                    ) : (
                      <div className="d-flex justify-content-center align-items-center gap-2">
                        <button className="icon-button" onClick={() => {
                          setIsEditing(product.productID);
                          setPriceChange({ [product.productID]: product.price });
                        }}>
                          <EditIcon style={{ color: 'black' }} />
                        </button>
                        <button className="icon-button" onClick={() => handleDeleteProduct(product)}>
                          <DeleteIcon className="delete-icon" />
                        </button>
                      </div>)}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
      </div>
    );
  };

  const MatboardTable = () => {
    const mbSortKey = matboardSort.key;
    const mbSortDir = matboardSort.dir;
    return (
      <div className="matboards-sortable-wrap">
      <Table striped bordered hover className="product-table" >
        <thead>
          <tr>
            <th style={{ width: "15%" }} scope="col">PREVIEW</th>
            <th
              style={{ width: "18%" }}
              scope="col"
              aria-sort={tableColumnAriaSort(mbSortKey, mbSortDir, 'productId')}
              className={`frame-sort-th ${mbSortKey === 'productId' ? 'frame-sort-th--active' : ''}`}
            >
              <FrameSortHeaderButton
                label="PRODUCT ID"
                sortKey="productId"
                activeKey={mbSortKey}
                activeDir={mbSortDir}
                onSort={handleMatboardSortClick}
              />
            </th>
            <th
              style={{ width: "37%" }}
              scope="col"
              aria-sort={tableColumnAriaSort(mbSortKey, mbSortDir, 'productName')}
              className={`frame-sort-th ${mbSortKey === 'productName' ? 'frame-sort-th--active' : ''}`}
            >
              <FrameSortHeaderButton
                label="PRODUCT NAME"
                sortKey="productName"
                activeKey={mbSortKey}
                activeDir={mbSortDir}
                onSort={handleMatboardSortClick}
              />
            </th>
            <th
              style={{ width: "30%" }}
              scope="col"
              aria-sort={tableColumnAriaSort(mbSortKey, mbSortDir, 'style')}
              className={`frame-sort-th ${mbSortKey === 'style' ? 'frame-sort-th--active' : ''}`}
            >
              <FrameSortHeaderButton
                label="MATBOARD STYLE"
                sortKey="style"
                activeKey={mbSortKey}
                activeDir={mbSortDir}
                onSort={handleMatboardSortClick}
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {paginatedProducts.map((product) => (
            <tr key={product.productID}>
              <td>
                <div className="d-flex justify-content-center align-items-center gap-2"
                  style={{
                    width: 50,
                    height: 50,
                    backgroundColor: product.hexColor, // Use the hex color for the square
                    border: "1px solid #000",
                    borderRadius: 4,
                    margin: '0 auto'
                  }} />
              </td>
              <td>{product.productID}</td>
              <td>{product.displayName}</td>
              <td>{product.style}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      </div>
    );
  };
  const renderProductBySizeTable = (tableProducts = paginatedProducts, tableUserType = userType) => {
    const colSpanCount = tableUserType !== 'employee' ? 2 : 1;

    const handleOpChange = (e, productID) => {
      setBulkOperations({ ...bulkOperations, [productID]: e.target.value });
    };

    const handleAmountChange = (e, productID) => {
      setBulkAmounts({ ...bulkAmounts, [productID]: e.target.value });
    };

    const handleApplyBulk = (productID) => {
      const op = bulkOperations[productID] || 'add';
      const amtStr = bulkAmounts[productID];
      console.log(`[Bulk Edit] Applying ${op} with amount ${amtStr} to ${productID}`);

      if (!amtStr) return;
      const amount = parseFloat(amtStr);
      if (isNaN(amount)) return;

      const productPrices = pricing[activeCategory][productID] || {};
      let currentChanges = priceChanges[productID] ? { ...priceChanges[productID] } : {};
      let currentDrafts = priceInputDrafts[productID] ? { ...priceInputDrafts[productID] } : {};

      imageSizeList.forEach(sizeObj => {
        const size = sizeObj.string;
        const basePrice = currentChanges[size] !== undefined
          ? coercePriceNumber(currentChanges[size])
          : coercePriceNumber(productPrices[size]);
        let newPrice = basePrice;

        switch (op) {
          case 'add': newPrice = basePrice + amount; break;
          case 'subtract': newPrice = Math.max(0, basePrice - amount); break;
          case 'multiply': newPrice = basePrice * amount; break;
          case 'set': newPrice = amount; break;
          default: break;
        }

        newPrice = Math.max(0, parseFloat(newPrice.toFixed(2)));

        // Record the change so handleSave will save them
        currentChanges[size] = newPrice;
        currentDrafts[size] = newPrice.toFixed(2);
      });

      setPriceChanges((prev) => ({
        ...prev,
        [productID]: currentChanges
      }));

      setPriceInputDrafts((prev) => ({
        ...prev,
        [productID]: currentDrafts
      }));

      // Clear amount to indicate success
      setBulkAmounts({ ...bulkAmounts, [productID]: '' });
    };
    return (
      <table className="by-size-table" >
        {activeCategory === 'glass' && (
          <thead>
            <tr>
              <th style={{ width: tableUserType !== 'employee' ? "90%" : "100%" }}>PRODUCT NAME</th>
              {tableUserType !== 'employee' && <th style={{ width: "10%", textAlign: "center" }}>ACTIONS</th>}
            </tr>
          </thead>
        )}
        <tbody>
          {tableProducts.map((product) => (
            <React.Fragment key={product.displayName}>
              {/* Product Name Row */}
              <tr>
                <td style={{ textAlign: "left", fontWeight: "bold" }}>{product.displayName}</td>
                {tableUserType !== 'employee' && (
                  <td style={{ width: "10%", textAlign: "center" }}>
                    <div className="d-flex justify-content-center align-items-center gap-2">
                      <button
                        className="icon-button"
                        onClick={() => {
                          handleEditPricing();
                        }}
                      >
                        <EditIcon style={{ color: "black" }} />
                      </button>
                    </div>
                  </td>
                )}
              </tr>

              {/* Size Chart Row - Always Visible */}
              <tr className="size-chart-row">
                <td colSpan={colSpanCount}>
                  <table className={`size-chart ${isEditingPrice ? "small" : "large"}`}>
                    <thead>
                      <tr>
                        {imageSizeList.map((size) => (
                          <th key={size.string}>{size.width} x {size.height}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {imageSizeList.map((size) => (
                          isEditingPrice ? (
                            <td key={size.string}>
                              <input
                                type="text"
                                className="price-input"
                                value={getSizePriceInputValue(product.displayName, size.string)}
                                onChange={(e) => handlePriceChange(e, product.displayName, size.string)}
                                onBlur={(e) => handleBlur(product.displayName, size.string)}
                                disabled={tableUserType === 'employee'}
                              />
                            </td>
                          ) : (
                            <td key={size.string}>
                              ${(pricing[activeCategory][product.displayName]?.[size.string] || 0).toFixed(2)}
                            </td>
                          )
                        ))}
                      </tr>
                    </tbody>
                  </table>
                  {isEditingPrice && tableUserType !== 'employee' && (
                    <div className="d-flex justify-content-between align-items-center mb-3 px-3">
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: '14px', fontWeight: 'bold' }}>Bulk Edit:</span>
                        <select className="form-select form-select-sm"
                          style={{ width: "auto" }}
                          value={bulkOperations[product.displayName] || 'add'}
                          onChange={(e) => handleOpChange(e, product.displayName)}>
                          <option value="add">Add (+)</option>
                          <option value="subtract">Subtract (-)</option>
                          <option value="multiply">Multiply (x)</option>
                          <option value="set">Set To (=)</option>
                        </select>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          style={{ width: "80px" }}
                          value={bulkAmounts[product.displayName] || ''}
                          onChange={(e) => handleAmountChange(e, product.displayName)}
                          placeholder="Amount"
                        />
                        <Button variant="outline-primary" size="sm" onClick={() => handleApplyBulk(product.displayName)}>Apply to All</Button>
                      </div>
                      <div className="d-flex gap-2">
                        <Button variant="primary" size="sm" onClick={() => handleSave(product.displayName)}>Save Changes</Button>
                        <Button variant="secondary" size="sm" onClick={handleCancel}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    );
  };

  const HangingTable = ({ userType }) => {
    const [isEditing, setIsEditing] = useState(null);
    const [priceChange, setPriceChange] = useState({});

    const handlePriceChange = (productName, newPrice) => {
      const parsedPrice = parseFloat(newPrice);
      if (parsedPrice >= 0) {
        setPriceChange({ [productName]: parsedPrice });
      }
    };
    const handleSave = (productName) => {
      setProducts((prevProducts) => {
        const updatedHanging = prevProducts.hanging.map((product) => {
          if (product.displayName === productName) {
            return { ...product, price: priceChange[productName] };
          }
          return product;
        });

        return { ...prevProducts, hanging: updatedHanging };
      });

      // Update in database 
      updateHangingPrice(productName, priceChange[productName]);

      setIsEditing(null);
      setPriceChange({});
    }

    const handleCancel = () => {
      setPriceChange({});
      setIsEditing(null);
    }
    return (
      <Table striped bordered hover className="product-table hanging" >
        <thead>
          <tr>
            <th style={{ width: "40%" }}>PRODUCT NAME</th>
            <th style={{ width: "30%" }}>PRICE</th>
            {userType !== 'employee' && <th style={{ width: "30%" }}>ACTIONS</th>}
          </tr>
        </thead>
        <tbody>
          {paginatedProducts.map((product) => (
            <tr key={product.displayName}>
              <td>{product.displayName}</td>
              {/* TODO: Add bulk operation for hanging frames */}
              {isEditing === product.displayName ? (
                <td key={product.displayName}>
                  <input
                    type="number"
                    className="price-input"
                    value={priceChange[product.displayName] ?? product.price}
                    onChange={(e) => handlePriceChange(product.displayName, e.target.value)}
                    disabled={userType === 'employee'}
                  />
                </td>
              ) :
                (<td>${(product.price).toFixed(2)}</td>)}
              {userType !== 'employee' && (
                <td>
                  <div className="d-flex justify-content-center align-items-center gap-2">
                    {isEditing === product.displayName ? (
                      <div className="d-flex justify-content-center align-items-center gap-2">
                        <Button variant="primary" size="sm" onClick={() => handleSave(product.displayName)}>Save</Button>
                        <Button variant="secondary" size="sm" onClick={() => handleCancel()}>Cancel</Button>
                      </div>
                    ) : (
                      <button className="icon-button" onClick={() => {
                        setIsEditing(product.displayName);
                        setPriceChange({ [product.displayName]: product.price });
                      }}>
                        <EditIcon style={{ color: 'black' }} />
                      </button>)}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };
  const ArtistFeeTable = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [tempFee, setTempFee] = useState(artistFee);

    const handleEdit = () => {
      setIsEditing(true);
      setTempFee(artistFee);
    };

    const handleSave = () => {
      setArtistFee(tempFee);
      updateArtistFee(tempFee);
      setIsEditing(false);
    };

    const handleCancel = () => {
      setIsEditing(false);
    };

    return (
      <div>
        <Table striped bordered hover className="product-table hanging" >
          <thead>
            <tr>
              <th style={{ width: "70%" }}>ARTIST FEE</th>
              {userType !== 'employee' && <th style={{ width: "30%" }}>ACTIONS</th>}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    value={tempFee}
                    onChange={(e) => setTempFee(parseFloat(e.target.value))}
                    className="form-control"
                  />
                ) : (
                  artistFee
                )}
              </td>
              {userType !== 'employee' && (
                <td>
                  <div className="d-flex justify-content-evenly">
                    {isEditing ? (
                      <>
                        <div className="d-flex justify-content-center align-items-center gap-2">
                          <Button variant="primary" size="sm" onClick={() => handleSave()}>Save</Button>
                          <Button variant="secondary" size="sm" onClick={() => handleCancel()}>Cancel</Button>
                        </div>
                      </>
                    ) : (
                      <button onClick={handleEdit} className="icon-button">
                        <EditIcon style={{ color: 'black' }} />
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          </tbody>
        </Table>
      </div>
    )
  }

  const renderCanvasTable = (tableUserType = userType) => {
    const stretchingStyles = ['1 1/2 inch thick', '3/4 inch thick'];
    const handleOpChange = (e, style) => {
      setCanvasBulkOperations(prev => ({ ...prev, [style]: e.target.value }));
    };

    const handleAmountChange = (e, style) => {
      setCanvasBulkAmounts(prev => ({ ...prev, [style]: e.target.value }));
    };

    const handleApplyBulk = (style) => {
      const op = canvasBulkOperations[style] || 'add';
      const amtStr = canvasBulkAmounts[style];
      if (!amtStr) return;
      const amount = parseFloat(amtStr);
      if (isNaN(amount)) return;

      const stylePrices = canvasPricing[style] || {};
      let currentChanges = canvasPriceChanges[style] ? { ...canvasPriceChanges[style] } : {};
      let currentDrafts = canvasPriceDrafts[style] ? { ...canvasPriceDrafts[style] } : {};

      imageSizeList.forEach(sizeObj => {
        const size = sizeObj.string;
        const basePrice = currentChanges[size] !== undefined
          ? coercePriceNumber(currentChanges[size])
          : coercePriceNumber(stylePrices[size]);
        let newPrice = basePrice;

        switch (op) {
          case 'add': newPrice = basePrice + amount; break;
          case 'subtract': newPrice = Math.max(0, basePrice - amount); break;
          case 'multiply': newPrice = basePrice * amount; break;
          case 'set': newPrice = amount; break;
          default: break;
        }

        newPrice = Math.max(0, parseFloat(newPrice.toFixed(2)));
        currentChanges[size] = newPrice;
        currentDrafts[size] = newPrice.toFixed(2);
      });

      setCanvasPriceChanges(prev => ({ ...prev, [style]: currentChanges }));
      setCanvasPriceDrafts(prev => ({ ...prev, [style]: currentDrafts }));
      setCanvasBulkAmounts(prev => ({ ...prev, [style]: '' }));
    };

    const colSpanCount = tableUserType !== 'employee' ? 2 : 1;

    return (
      <table className="by-size-table">
        <thead>
          <tr>
            <th style={{ width: tableUserType !== 'employee' ? "90%" : "100%" }}>PRODUCT NAME</th>
            {tableUserType !== 'employee' && <th style={{ width: "10%", textAlign: "center" }}>ACTIONS</th>}
          </tr>
        </thead>
        <tbody>
          {stretchingStyles.map(style => (
            <React.Fragment key={style}>
              <tr>
                <td style={{ textAlign: "left", fontWeight: "bold" }}>Canvas — {style}</td>
                {tableUserType !== 'employee' && (
                  <td>
                    <div className="d-flex justify-content-center align-items-center gap-2">
                      <button
                        className="icon-button"
                        onClick={handleCanvasEditPricing}
                      >
                        <EditIcon style={{ color: "black" }} />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
              <tr className="size-chart-row">
                <td colSpan={colSpanCount}>
                  <table className={`size-chart ${isEditingCanvasPrice ? "small" : "large"}`}>
                    <thead>
                      <tr>
                        {imageSizeList.map(size => (
                          <th key={size.string}>{size.width} x {size.height}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {imageSizeList.map(size => (
                          isEditingCanvasPrice ? (
                            <td key={size.string}>
                              <input
                                type="text"
                                className="price-input"
                                value={getCanvasPriceInputValue(style, size.string)}
                                onChange={(e) => handleCanvasPriceChange(e, style, size.string)}
                                onBlur={() => handleCanvasBlur(style, size.string)}
                                disabled={tableUserType === 'employee'}
                              />
                            </td>
                          ) : (
                            <td key={size.string}>
                              ${(canvasPricing[style]?.[size.string] ?? 0).toFixed(2)}
                            </td>
                          )
                        ))}
                      </tr>
                    </tbody>
                  </table>
                  {isEditingCanvasPrice && tableUserType !== 'employee' && (
                    <div className="d-flex justify-content-between align-items-center mb-3 px-3">
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: '14px', fontWeight: 'bold' }}>Bulk Edit:</span>
                        <select
                          className="form-select form-select-sm"
                          style={{ width: "auto" }}
                          value={canvasBulkOperations[style] || 'add'}
                          onChange={(e) => handleOpChange(e, style)}
                        >
                          <option value="add">Add (+)</option>
                          <option value="subtract">Subtract (-)</option>
                          <option value="multiply">Multiply (x)</option>
                          <option value="set">Set To (=)</option>
                        </select>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          style={{ width: "80px" }}
                          value={canvasBulkAmounts[style] || ''}
                          onChange={(e) => handleAmountChange(e, style)}
                          placeholder="Amount"
                        />
                        <Button variant="outline-primary" size="sm" onClick={() => handleApplyBulk(style)}>
                          Apply to All
                        </Button>
                      </div>
                      <div className="d-flex gap-2">
                        <Button variant="primary" size="sm" onClick={() => handleCanvasSave(style)}>
                          Save Changes
                        </Button>
                        <Button variant="secondary" size="sm" onClick={handleCanvasCancel}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    );
  };

  const renderProductTable = () => {
    // Matboard Style Display Names for pricing
    const matboardStyle = ADMIN_MATBOARD_PRICING_STYLES
      .filter((style) => pricing?.matboards?.[style] != null)
      .map((style) => ({ displayName: style }));

    if (loading) {
      return <Loading />;
    }
    else {
      if (activeCategory === 'frames') {
        return <FramesTable handleDeleteProduct={handleDeleteProduct} userType={userType} />;
      }
      if (activeCategory === 'matboards' && showMatboardPrices === false) {
        return <MatboardTable />;
      }
      else if (activeCategory === 'matboards' && showMatboardPrices === true) {
        return renderProductBySizeTable(matboardStyle, userType);
      }
      if (activeCategory === 'mediums') {
        return (
          <div>
            {renderCanvasTable(userType)}
            {renderProductBySizeTable([{ displayName: 'Photo Print' }], userType)}
          </div>
        );
      }
      if (activeCategory === 'glass') {
        return renderProductBySizeTable(paginatedProducts, userType);
      }
      if (activeCategory === 'hanging') {
        return <HangingTable userType={userType} />
      }
      if (activeCategory === 'artist fee') {
        return <ArtistFeeTable userType={userType} />
      }
      return null;
    }
  };

  const handleProductSaved = (productData) => {
    setProducts((prevProducts) => {
      const updatedCategory = [productData, ...prevProducts[activeCategory]];
      return { ...prevProducts, [activeCategory]: updatedCategory };
    });
  };

  return (
    <div>
      <AdminNavbar />
      <hr className="admin-divider" />
      <div className="product-page">
        <Sidebar userType={userType || 'admin'} />
        <div className="product-content">
          {/* Line 1: View Only Banner (centered) */}
          {userType === 'employee' && (
            <div className="view-only-banner">
              <VisibilityIcon style={{ fontSize: '18px' }} />
              <span>Employee accounts have read-only access to product information.</span>
            </div>
          )}

          {/* Line 2: Category boxes (left) + Search/Button (right) */}
          <div className="header-row">
            <div className="category-boxes">
              {categories.map((category) => (
                <div
                  key={category}
                  className={`category-box ${activeCategory === category ? 'active' : ''}`}
                  onClick={() => {
                    setActiveCategory(category);
                    setShowMatboardPrices(false);
                    setIsEditingPrice(false);
                    setPriceChanges({});
                    setPriceInputDrafts({});
                    setBulkOperations({});
                    setBulkAmounts({});
                    setIsEditingCanvasPrice(false);
                    setCanvasPriceChanges({});
                    setCanvasPriceDrafts({});
                    setCanvasBulkOperations({});
                    setCanvasBulkAmounts({});
                    setCurrentPage(1);
                  }}
                >
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </div>
              ))}
            </div>

            <div className="header-btns">
              {(activeCategory === 'frames' || activeCategory === 'matboards') && (
                <input
                  type="text"
                  className="search-box"
                  placeholder="Search by name or ID..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              )}

              {activeCategory === 'frames' && userType !== 'employee' && (
                <button className="btn btn-primary"
                  onClick={() => {
                    setShowProductModal(true);
                  }}
                > Add New Product </button>
              )}

              {activeCategory === 'matboards' && showMatboardPrices === false && userType !== 'employee' ? (
                <button className="btn btn-primary"
                  onClick={() => setShowMatboardPrices(true)}>
                  Edit Price List
                </button>
              ) : activeCategory === 'matboards' && showMatboardPrices === true ? (
                <button className="btn btn-primary"
                  onClick={() => setShowMatboardPrices(false)}>
                  Back to Matboards
                </button>
              ) : null}
            </div>
          </div>

          <div className="product-table-container">
            {renderProductTable()}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && !showMatboardPrices && (
            <div className="pagination">
              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Prev
              </button>
              <span>
                {`Showing ${(currentPage - 1) * itemsPerPage + 1} - ${Math.min(
                  currentPage * itemsPerPage,
                  filteredProducts.length
                )} of ${filteredProducts.length}`}
              </span>
              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal for Product */}
      {showProductModal && (
        <ProductModal
          handleImageUpload={handleImageUpload}
          onProductSaved={handleProductSaved}
        />
      )}
    </div>
  );
};

export default ProductPage;
